import React, { useState, useEffect, useCallback } from 'react';
import {
  addHoliday,
  deleteHoliday,
  getHolidaysByYear,
  updateAttendanceRecordsWithHoliday
} from '../firebase/firestore';

function HolidayManagement() {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [holidayName, setHolidayName] = useState('');
  const [updating, setUpdating] = useState(false);

  // 공휴일 데이터 로드
  const loadHolidays = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getHolidaysByYear(selectedYear);
      if (result.success) {
        // 날짜순으로 정렬
        const sortedHolidays = result.holidays.sort((a, b) => {
          return new Date(a.date) - new Date(b.date);
        });
        setHolidays(sortedHolidays);
      } else {
        console.error('공휴일 조회 오류:', result.error);
      }
    } catch (error) {
      console.error('공휴일 조회 오류:', error);
    }
    setLoading(false);
  }, [selectedYear]);

  useEffect(() => {
    loadHolidays();
  }, [loadHolidays]);

  // 공휴일 추가
  const handleAddHoliday = async (e) => {
    e.preventDefault();

    if (!selectedDate || !holidayName.trim()) {
      alert('날짜와 공휴일 이름을 모두 입력해주세요.');
      return;
    }

    // 이미 등록된 날짜인지 확인
    const existingHoliday = holidays.find(h => h.date === selectedDate);
    if (existingHoliday) {
      alert('이미 해당 날짜에 공휴일이 등록되어 있습니다.');
      return;
    }

    setUpdating(true);
    try {
      const dateObj = new Date(selectedDate);
      const holidayData = {
        date: selectedDate,
        name: holidayName.trim(),
        year: dateObj.getFullYear(),
        month: dateObj.getMonth() + 1,
        day: dateObj.getDate()
      };

      const result = await addHoliday(holidayData);

      if (result.success) {
        // 과거 출퇴근 기록에 공휴일 플래그 추가
        const updateResult = await updateAttendanceRecordsWithHoliday(selectedDate, true);

        alert(
          `공휴일이 등록되었습니다.\n${updateResult.message || '출퇴근 기록이 업데이트되었습니다.'}`
        );

        // 폼 초기화 및 데이터 새로고침
        setSelectedDate('');
        setHolidayName('');
        setShowAddForm(false);
        loadHolidays();
      } else {
        alert('공휴일 등록 중 오류가 발생했습니다.');
      }
    } catch (error) {
      console.error('공휴일 등록 오류:', error);
      alert('공휴일 등록 중 오류가 발생했습니다.');
    }
    setUpdating(false);
  };

  // 공휴일 삭제
  const handleDeleteHoliday = async (holidayId, date) => {
    if (!window.confirm('정말로 이 공휴일을 삭제하시겠습니까?\n해당 날짜의 출퇴근 기록도 함께 업데이트됩니다.')) {
      return;
    }

    setUpdating(true);
    try {
      const result = await deleteHoliday(holidayId);

      if (result.success) {
        // 출퇴근 기록에서 공휴일 플래그 제거
        const updateResult = await updateAttendanceRecordsWithHoliday(date, false);

        alert(
          `공휴일이 삭제되었습니다.\n${updateResult.message || '출퇴근 기록이 업데이트되었습니다.'}`
        );
        loadHolidays();
      } else {
        alert('공휴일 삭제 중 오류가 발생했습니다.');
      }
    } catch (error) {
      console.error('공휴일 삭제 오류:', error);
      alert('공휴일 삭제 중 오류가 발생했습니다.');
    }
    setUpdating(false);
  };

  // 날짜 포맷 변환 함수
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const weekdays = ['일', '월', '화', '수', '목', '금', '토'];
    const weekday = weekdays[date.getDay()];
    return `${year}-${month}-${day} (${weekday})`;
  };

  // 년도 옵션 생성 (현재 년도 ±3년)
  const yearOptions = Array.from({ length: 7 }, (_, idx) => selectedYear - 3 + idx);

  return (
    <div className="container mx-auto p-4">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold">공휴일 관리</h2>
            <p className="text-sm text-gray-600 mt-1">
              공휴일을 등록하면 해당 날짜의 근무시간에 자동으로 1.5배 수당이 적용됩니다.
            </p>
          </div>
          <button
            onClick={() => {
              setShowAddForm(!showAddForm);
              if (showAddForm) {
                setSelectedDate('');
                setHolidayName('');
              }
            }}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            disabled={updating}
          >
            {showAddForm ? '취소' : '공휴일 추가'}
          </button>
        </div>

        {/* 년도 선택 */}
        <div className="mb-6">
          <label className="block text-sm font-medium mb-2">조회 년도:</label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="px-4 py-2 border rounded-lg"
            disabled={updating}
          >
            {yearOptions.map((year) => (
              <option key={year} value={year}>
                {year}년
              </option>
            ))}
          </select>
        </div>

        {/* 공휴일 추가 폼 */}
        {showAddForm && (
          <div className="mb-6 p-4 border rounded bg-gray-50">
            <h3 className="text-lg font-bold mb-4">공휴일 추가</h3>
            <form onSubmit={handleAddHoliday}>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">날짜 *</label>
                  <input
                    type="date"
                    required
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="w-full px-3 py-2 border rounded"
                    disabled={updating}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">공휴일 이름 *</label>
                  <input
                    type="text"
                    required
                    value={holidayName}
                    onChange={(e) => setHolidayName(e.target.value)}
                    placeholder="예: 설날, 추석, 근로자의날"
                    className="w-full px-3 py-2 border rounded"
                    disabled={updating}
                  />
                </div>
              </div>

              <div className="bg-blue-50 p-3 rounded mb-4">
                <p className="text-sm">
                  💡 공휴일을 등록하면 해당 날짜에 등록된 모든 출퇴근 기록(과거 포함)에
                  공휴일 근무 수당(1.5배)이 자동으로 적용됩니다.
                </p>
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setSelectedDate('');
                    setHolidayName('');
                  }}
                  className="px-4 py-2 border rounded hover:bg-gray-100"
                  disabled={updating}
                >
                  취소
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300"
                  disabled={updating}
                >
                  {updating ? '처리 중...' : '저장'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* 공휴일 목록 */}
        <div className="overflow-x-auto">
          <table className="w-full table-auto border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border px-4 py-2">날짜</th>
                <th className="border px-4 py-2">공휴일 이름</th>
                <th className="border px-4 py-2">작업</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="3" className="border px-4 py-8 text-center">
                    로딩 중...
                  </td>
                </tr>
              ) : holidays.length > 0 ? (
                holidays.map((holiday) => (
                  <tr key={holiday.id} className="hover:bg-gray-50">
                    <td className="border px-4 py-2 text-center">
                      {formatDate(holiday.date)}
                    </td>
                    <td className="border px-4 py-2 font-bold">
                      {holiday.name}
                    </td>
                    <td className="border px-4 py-2 text-center">
                      <button
                        onClick={() => handleDeleteHoliday(holiday.id, holiday.date)}
                        className="bg-red-500 text-white px-3 py-1 rounded text-sm hover:bg-red-600 disabled:bg-gray-300"
                        disabled={updating}
                      >
                        삭제
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" className="border px-4 py-8 text-center text-gray-500">
                    {selectedYear}년에 등록된 공휴일이 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* 통계 정보 */}
        {holidays.length > 0 && (
          <div className="mt-4 p-4 bg-blue-50 rounded">
            <p className="text-sm">
              📊 {selectedYear}년 총 공휴일: <strong>{holidays.length}일</strong>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default HolidayManagement;
